import { useMemo, useState } from "react";
import { Plus, Search, Filter, Paperclip, ChevronRight, MessagesSquare, ListChecks, AlertTriangle, Clock, Ban, CheckCircle2, Flame } from "lucide-react";
import { Section, Empty, SelectPill, KpiGrid } from "./CommonUI";
import { CR_STATUS_TONE, CR_SEVERITY_TONE, fmtDateTime } from "./clientUtils";
import ClientCRCreateModal from "./ClientCRCreateModal";
import ClientCRDetailModal from "./ClientCRDetailModal";

export default function ChangeRequestsView({ rows, loading, onRefresh, token, projects = [], clientId = "" }) {
  const [q, setQ] = useState("");
  const [status, setStatus] = useState("all");
  const [severity, setSeverity] = useState("all");
  const [showCreate, setShowCreate] = useState(false);
  const [openCr, setOpenCr] = useState(null);

  const filtered = useMemo(() => rows.filter((r) => {
    if (status !== "all" && r.status !== status) return false;
    if (severity !== "all" && r.severity !== severity) return false;
    if (q) {
      const hay = (`${r.title} ${r.project_name} ${r.raised_by_name || ""}`).toLowerCase();
      if (!hay.includes(q.toLowerCase())) return false;
    }
    return true;
  }), [rows, q, status, severity]);

  // KPI counts — always computed over the full CR set for stability, so cards
  // show the true state of the client's CRs regardless of the current filter.
  const kpi = useMemo(() => {
    const c = { total: rows.length, open: 0, inprogress: 0, blocked: 0, done: 0, critical: 0 };
    for (const r of rows) {
      if (r.status === "Open") c.open += 1;
      else if (r.status === "In Progress") c.inprogress += 1;
      else if (r.status === "Blocked") c.blocked += 1;
      else if (r.status === "Done") c.done += 1;
      if (r.severity === "Critical" && r.status !== "Done" && r.status !== "Rejected") c.critical += 1;
    }
    return c;
  }, [rows]);

  return (
    <>
      <KpiGrid cols="grid-cols-2 md:grid-cols-3 lg:grid-cols-6" cards={[
        {
          label: "Total CRs", value: kpi.total,
          icon: <ListChecks className="h-4 w-4 text-[#0a0a0a]" />,
          tone: "bg-[#0a0a0a]",
        },
        {
          label: "Open", value: kpi.open,
          icon: <AlertTriangle className="h-4 w-4 text-sky-600" />,
          tone: "bg-sky-500", accent: "text-sky-700",
        },
        {
          label: "In Progress", value: kpi.inprogress,
          icon: <Clock className="h-4 w-4 text-amber-600" />,
          tone: "bg-amber-500", accent: "text-amber-700",
        },
        {
          label: "Blocked", value: kpi.blocked,
          icon: <Ban className="h-4 w-4 text-rose-600" />,
          tone: "bg-rose-500", accent: "text-rose-700",
        },
        {
          label: "Done", value: kpi.done,
          icon: <CheckCircle2 className="h-4 w-4 text-emerald-600" />,
          tone: "bg-emerald-500", accent: "text-emerald-700",
        },
        {
          label: "Critical · Active", value: kpi.critical,
          icon: <Flame className="h-4 w-4 text-rose-600" />,
          tone: "bg-rose-600", accent: "text-rose-700",
        },
      ]} />
    <Section title="Change Requests" subtitle="Every project change your team has raised or is tracking."
      onRefresh={onRefresh} loading={loading}
      toolbar={
        <div className="flex flex-wrap items-center gap-2">
          <button onClick={() => setShowCreate(true)} data-testid="client-cr-new"
            className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs bg-[#ff5722] text-white rounded-full hover:bg-[#0a0a0a] transition-colors">
            <Plus className="h-3.5 w-3.5" /> Raise CR
          </button>
          <div className="relative">
            <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-[#9a9a9a]" />
            <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search title / project / raised by…"
              data-testid="client-crs-search"
              className="font-mono text-xs border border-black/15 rounded-full pl-8 pr-3 py-1.5 w-64 outline-none focus:border-[#ff5722] focus:ring-2 focus:ring-[#ff5722]/20" />
          </div>
          <SelectPill icon={<Filter className="h-3.5 w-3.5 text-[#9a9a9a]" />} prefix="Status"
            value={status} onChange={setStatus}
            options={["all", "Open", "In Progress", "Done", "Blocked", "Rejected"]}
            fmt={(v) => v === "all" ? "All" : v} testid="client-crs-filter-status" />
          <SelectPill icon={<Filter className="h-3.5 w-3.5 text-[#9a9a9a]" />} prefix="Severity"
            value={severity} onChange={setSeverity}
            options={["all", "Critical", "Major", "Minor"]}
            fmt={(v) => v === "all" ? "All" : v} testid="client-crs-filter-severity" />
        </div>
      }>
      {filtered.length === 0 ? (
        <Empty msg={rows.length === 0 ? "No change requests yet — your team hasn’t raised any." : "No CRs match your filters."} />
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-left min-w-[900px]">
            <thead className="bg-[#f7f6f3] border-b border-black/10 text-[11px] font-mono uppercase tracking-[0.2em] text-[#4a4a4a]">
              <tr>
                <th className="px-4 py-3 w-12">#</th>
                <th className="px-4 py-3">Title / Project</th>
                <th className="px-4 py-3">Severity</th>
                <th className="px-4 py-3">Status</th>
                <th className="px-4 py-3">Raised by</th>
                <th className="px-4 py-3">Raised on</th>
                <th className="px-4 py-3 text-center">Docs</th>
                <th className="px-4 py-3 text-center">Chat</th>
                <th className="px-4 py-3 text-right w-12" />
              </tr>
            </thead>
            <tbody>
              {filtered.map((cr, i) => (
                <tr key={cr.id} data-testid={`client-cr-row-${cr.id}`}
                  onClick={() => setOpenCr(cr)}
                  className="border-b border-black/5 cursor-pointer hover:bg-[#ff5722]/[0.04] transition-colors">
                  <td className="px-4 py-4 text-xs font-mono text-[#4a4a4a]">{String(i + 1).padStart(2, "0")}</td>
                  <td className="px-4 py-4">
                    <div className="font-semibold text-[#0a0a0a] line-clamp-1">{cr.title}</div>
                    <div className="text-[11px] text-[#4a4a4a]">{cr.project_name || "—"}</div>
                  </td>
                  <td className="px-4 py-4">
                    <span className={`inline-block whitespace-nowrap px-2.5 py-0.5 text-[10px] font-mono uppercase tracking-[0.1em] border rounded-full ${CR_SEVERITY_TONE[cr.severity] || "bg-zinc-100 border-zinc-300"}`}>{cr.severity}</span>
                  </td>
                  <td className="px-4 py-4">
                    <span className={`inline-block whitespace-nowrap px-2.5 py-0.5 text-[10px] font-mono uppercase tracking-[0.1em] border rounded-full ${CR_STATUS_TONE[cr.status] || "bg-zinc-100 border-zinc-300"}`}>{cr.status}</span>
                  </td>
                  <td className="px-4 py-4 text-xs">
                    <span className={`font-semibold ${cr.raised_by === "client" ? "text-[#ff5722]" : "text-[#0a0a0a]"}`}>
                      {cr.raised_by_name || (cr.raised_by === "client" ? "Client" : "Reachvel")}
                    </span>
                  </td>
                  <td className="px-4 py-4 text-xs font-mono text-[#4a4a4a] whitespace-nowrap">{fmtDateTime(cr.raised_at || cr.created_at || cr.raised_date)}</td>
                  <td className="px-4 py-4 text-center text-xs">
                    {cr.documents_count > 0
                      ? <span className="inline-flex items-center gap-1 text-[#0a0a0a]"><Paperclip className="h-3 w-3" /> {cr.documents_count}</span>
                      : <span className="text-[#9a9a9a]">—</span>}
                  </td>
                  <td className="px-4 py-4 text-center text-xs">
                    <span className="inline-flex items-center gap-1.5 justify-center">
                      <MessagesSquare className={`h-3.5 w-3.5 ${cr.comments_count > 0 ? "text-[#0a0a0a]" : "text-[#9a9a9a]"}`} />
                      {cr.comments_count > 0 ? <span className="text-[#0a0a0a]">{cr.comments_count}</span> : <span className="text-[#9a9a9a]">—</span>}
                      {cr.unread_count > 0 && (
                        <span
                          data-testid={`client-cr-unread-${cr.id}`}
                          className="inline-flex items-center justify-center min-w-[18px] h-[18px] px-1 rounded-full bg-[#ff5722] text-white text-[10px] font-bold"
                          title={`${cr.unread_count} new message${cr.unread_count > 1 ? "s" : ""} from Reachvel`}
                        >
                          {cr.unread_count}
                        </span>
                      )}
                    </span>
                  </td>
                  <td className="px-4 py-4 text-right">
                    <ChevronRight className="inline h-4 w-4 text-[#9a9a9a]" />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {showCreate && (
        <ClientCRCreateModal
          projects={projects}
          token={token}
          onClose={() => setShowCreate(false)}
          onCreated={() => { setShowCreate(false); onRefresh && onRefresh(); }}
        />
      )}
      {openCr && (
        <ClientCRDetailModal cr={openCr} token={token} clientId={clientId} onClose={() => { setOpenCr(null); onRefresh && onRefresh(); }} />
      )}
    </Section>
    </>
  );
}
